import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { baseRelatedPspBranchActions } from '../actions';
import { BaseCoreState } from '../reducers';
import { baseAuthSelectors } from '../selectors';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { BaseTokenInterface } from '../../shared/interfaces/base-token-service.interface';
import { BaseRelatedPspBranchesService } from '../../modules/related-psp-branches/base-related-psp-branches.service';
import { BaseFileDownloadService } from '../../shared/services/base-file-download.service';
import { BaseFileExtension } from '../../shared/enums/base-available-file-type.enum';

@Injectable()
export class BaseRelatedPspBranchesEffects {
  loadRelatedPspBranches$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseRelatedPspBranchActions.loadRelatedPspBranchList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.relatedPspBranchesService
          .relatedCustomerBranchList({
            customer: user.organizationId,
            ...action.payload,
          })
          .pipe(
            mergeMap((response) => [
              baseRelatedPspBranchActions.setRelatedPspBranchList({ payload: response }),
              baseRelatedPspBranchActions.successRelatedPspBranchList(),
            ]),
            catchError(() => of(baseRelatedPspBranchActions.errorRelatedPspBranchList()))
          );
      })
    );
  });

  loadRelatedPspBranchDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseRelatedPspBranchActions.loadRelatedPspBranchDetails),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.relatedPspBranchesService
          .relatedPspBranchDetails({
            customer: user.organizationId,
            psp: action.payload.psp,
            branch: action.payload.branch,
            location: action.payload.location,
          })
          .pipe(
            mergeMap((response: any) => [
              baseRelatedPspBranchActions.setRelatedPspBranchDetails({ payload: response }),
              baseRelatedPspBranchActions.successRelatedPspBranchDetails(),
            ]),
            catchError(() => of(baseRelatedPspBranchActions.errorRelatedPspBranchDetails()))
          );
      })
    );
  });

  loadRelatedPspBranchDocuments$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseRelatedPspBranchActions.loadRelatedPspBranchDocuments),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.relatedPspBranchesService
          .relatedPspBranchDocuments({
            customer: user.organizationId,
            psp: action.payload.psp,
          })
          .pipe(
            mergeMap((response: any) => [
              baseRelatedPspBranchActions.setRelatedPspBranchDocuments({ payload: response }),
              baseRelatedPspBranchActions.successRelatedPspBranchDocuments(),
            ]),
            catchError(() => of(baseRelatedPspBranchActions.errorRelatedPspBranchDocuments()))
          );
      })
    );
  });

  downloadRelatedPspBranchDocument$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseRelatedPspBranchActions.downloadRelatedPspBranchDocument),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const source$ = this.relatedPspBranchesService.downloadDocument({
          ...action.payload,
          customer: user.organizationId,
        });

        return source$.pipe(
          switchMap((response) =>
            this.fileDownloadService
              .downloadFile(of(response), {
                isPreview: action.payload.openInNewWindow,
                documentType: action.payload.documentType,
              })
              .pipe(
                mergeMap((blob) => {
                  const actions: Action[] = [baseRelatedPspBranchActions.successDownloadRelatedPspBranchDocument()];

                  if (blob) {
                    actions.unshift(
                      baseRelatedPspBranchActions.setDownloadRelatedPspBranchDocument({
                        payload: { document: blob },
                      })
                    );
                  }

                  return actions;
                })
              )
          ),
          catchError(() => of(baseRelatedPspBranchActions.errorDownloadRelatedPspBranchDocument()))
        );
      })
    );
  });

  downloadRelatedPspBranchContract$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(baseRelatedPspBranchActions.downloadRelatedPspBranchContract),
        withLatestFrom(this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data))),
        switchMap(([action, user]) => {
          return this.relatedPspBranchesService
            .downloadContract({
              ...action.payload,
              customer: user?.organizationId || -1,
            })
            .pipe(
              map((response) => {
                this.fileDownloadService.triggerBrowserDownload(
                  response,
                  action.payload.fileName || 'Contract',
                  BaseFileExtension.pdf
                );
              })
            );
        })
      ),
    { dispatch: false }
  );

  loadRelatedPspBranchSurcharges$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseRelatedPspBranchActions.loadSurcharges),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.relatedPspBranchesService
          .surchargesList({
            customer: user.organizationId,
            psp: action.payload,
          })
          .pipe(
            mergeMap((response: any) => [
              baseRelatedPspBranchActions.setSurcharges({ payload: response }),
              baseRelatedPspBranchActions.successSurcharges(),
            ]),
            catchError(() => of(baseRelatedPspBranchActions.errorSurcharges()))
          );
      })
    );
  });

  loadRelatedPspBranchData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseRelatedPspBranchActions.loadRelatedPspBranchData),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.relatedPspBranchesService
          .relatedPspBranchData({
            customer: user.organizationId,
            psp: action.payload.psp,
          })
          .pipe(
            mergeMap((response: any) => [
              baseRelatedPspBranchActions.setRelatedPspBranchData({ payload: response }),
              baseRelatedPspBranchActions.successRelatedPspBranchData(),
            ]),
            catchError(() => of(baseRelatedPspBranchActions.errorRelatedPspBranchData()))
          );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private store: Store<BaseCoreState>,
    private relatedPspBranchesService: BaseRelatedPspBranchesService,
    private fileDownloadService: BaseFileDownloadService
  ) {}
}
